import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  Users,
  ClipboardList,
  CheckCircle,
  Scale
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import approvalWorkflowMasterService from '../../services/approvalWorkflowMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ApprovalWorkflowMaster = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalWorkflows: 0,
    activeWorkflows: 0,
    inactiveWorkflows: 0,
    contractApprovalType: 0,
    legalReviewType: 0,
    financialApprovalType: 0,
    totalUsage: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [workflowsResponse, statsResponse] = await Promise.all([
        approvalWorkflowMasterService.getAllApprovalWorkflows(),
        approvalWorkflowMasterService.getApprovalWorkflowStats()
      ])
      
      if (workflowsResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (workflowsResponse.data || []).map(workflow => ({
          ...workflow,
          id: workflow._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (workflowsResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No approval workflow records found. Create your first workflow!')
        }
      } else {
        toast.error(workflowsResponse.message || 'Failed to load approval workflows')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading approval workflow master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view approval workflows')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load approval workflow master records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const getWorkflowTypeBadgeClass = (type) => {
    const typeMap = {
      'Contract Approval': 'active',
      'Legal Review': 'warning',
      'Financial Approval': 'pending',
      'Executive Approval': 'active',
      'Multi-Level': 'pending',
      'Custom': 'inactive'
    }
    return typeMap[type] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'workflowCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'workflowName',
      header: 'Workflow Name',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'workflowType',
      header: 'Type',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getWorkflowTypeBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'approvalStages',
      header: 'Stages',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center', fontWeight: '600' }}>
          {getValue()?.length || 0}
        </div>
      ),
    },
    {
      accessorKey: 'usageCount',
      header: 'Usage',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center', fontWeight: '600' }}>
          {getValue() || 0}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue()?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/contracts/approval-workflow-master/form')
  }

  const handleEdit = (workflowData) => {
    navigate('/contracts/approval-workflow-master/edit', { state: { workflowData } })
  }

  const handleView = (workflowData) => {
    navigate('/contracts/approval-workflow-master/view', { state: { workflowData } })
  }

  const handleDelete = (workflowData) => {
    setDeleteItem(workflowData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting approval workflow...')
      const response = await approvalWorkflowMasterService.deleteApprovalWorkflow(deleteItem._id)
      if (response.success) {
        toast.success('Approval workflow deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete approval workflow')
      }
    } catch (error) {
      console.error('Error deleting approval workflow:', error)
      if (error.response?.status === 404) {
        toast.error('Approval workflow not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this approval workflow')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete approval workflow. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Contracts', path: '/contracts' },
          { label: 'Approval Workflows', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Approval Workflows</h1>
          <p>Contract approval processes and signatories</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Workflow
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <ClipboardList size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalWorkflows || 0}</div>
          <div className="metric-title">Total Workflows</div>
          <div className="metric-subtitle">All approval workflows</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeWorkflows || 0}</div>
          <div className="metric-title">Active Workflows</div>
          <div className="metric-subtitle">Currently in use</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Contract</span>
            </div>
          </div>
          <div className="metric-value">{stats.contractApprovalType || 0}</div>
          <div className="metric-title">Contract Approval</div>
          <div className="metric-subtitle">Contract sign-off flows</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Scale size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Legal</span>
            </div>
          </div>
          <div className="metric-value">{stats.legalReviewType || 0}</div>
          <div className="metric-title">Legal Review</div>
          <div className="metric-subtitle">Legal review workflows</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Usage</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalUsage || 0}</div>
          <div className="metric-title">Total Usage</div>
          <div className="metric-subtitle">Times applied</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Approval Workflow"
        message={`Are you sure you want to delete approval workflow "${deleteItem?.workflowName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ApprovalWorkflowMaster

